"use client";

import React, { FC, PropsWithChildren, useCallback, useEffect } from "react";
import axios from "axios";
import {
  GlobalStateProvider,
  ShaApplicationProvider,
  StoredFilesProvider,
  useNextRouter,
} from "@shesha-io/reactjs";
import { AppProgressBar } from "next-nprogress-bar";
import { useTheme } from "antd-style";
import { BrandPlugin } from "@/definitions/brand/BrandPlugin";
import { SetupPlugin } from "@/definitions/setup/SetupPlugin";
import { useTenantResolver } from "@/definitions/brand/useTenantResolver";
import { CustomComponentsPlugin } from "@/providers/customComponentsPlugin";

export interface IAppProviderProps {
  backendUrl: string;
}

export const AppProvider: FC<PropsWithChildren<IAppProviderProps>> = ({
  children,
  backendUrl,
}) => {
  const nextRouter = useNextRouter();
  const theme = useTheme();

  // Resolve the brand (pbf/pcf from the hostname) to its tenant id and attach the Abp.TenantId
  // header on every request — including the pre-auth /TokenAuth/Authenticate call. This is the
  // header key the backend tenant resolver honors (verified). 'default' brand → no header (host).
  const tenantId = useTenantResolver(backendUrl);
  const buildHttpRequestHeaders = useCallback(
    () => (tenantId != null ? { "Abp.TenantId": String(tenantId) } : {}),
    [tenantId]
  );

  // buildHttpRequestHeaders only reaches the auth/CRUD clients — form-script `http` calls and
  // component URL data-sources (used all through the password-reset flow) bypass it, so their
  // requests would go out with no tenant and 500 for brand users. The Abp.TenantId cookie also
  // doesn't cross from pbf.localhost to the backend origin. Stamp the resolved tenant onto every
  // axios request so the whole flow carries the brand context.
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      if (tenantId != null) {
        // axios v1 request interceptors always receive an AxiosHeaders instance.
        if (!config.headers["Abp.TenantId"]) {
          config.headers["Abp.TenantId"] = String(tenantId);
        }
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [tenantId]);

  // Some Shesha component data-sources (e.g. the radio URL data-source on the reset-method screen)
  // fetch via native `fetch`, bypassing the axios interceptor above — so stamp the tenant onto
  // backend `/api/` fetch calls too. Scoped to /api/ so Next.js's own fetches are untouched.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const originalFetch = window.fetch;
    window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
      try {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
            ? input.href
            : (input as Request)?.url ?? "";
        if (tenantId != null && url.includes("/api/")) {
          const headers = new Headers(
            init?.headers ?? (input instanceof Request ? input.headers : undefined)
          );
          if (!headers.has("Abp.TenantId")) {
            headers.set("Abp.TenantId", String(tenantId));
            init = { ...init, headers };
          }
        }
      } catch {
        /* fall through to the original fetch */
      }
      return originalFetch.call(this, input as RequestInfo, init);
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, [tenantId]);

  return (
    <GlobalStateProvider>
      <AppProgressBar height="4px" color={theme.colorPrimary} shallowRouting />
      <ShaApplicationProvider
        backendUrl={backendUrl}
        router={nextRouter}
        noAuth={nextRouter.path?.includes('/no-auth')}
        buildHttpRequestHeaders={buildHttpRequestHeaders}
      >
        <BrandPlugin />
        <SetupPlugin />
        <CustomComponentsPlugin>
          <StoredFilesProvider baseUrl={backendUrl} ownerId={""} ownerType={""}>
            {children}
          </StoredFilesProvider>
        </CustomComponentsPlugin>
      </ShaApplicationProvider>
    </GlobalStateProvider>
  );
};
